/**
 * FIEligibilityGate — /fi/eligibility
 * Financial Institution Climate Finance Eligibility Engine — Gate 0
 * Hard gate: deal cannot proceed to structuring without passing.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, Shield } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const STATUS_ICONS = {
  pass:    <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />,
  warning: <AlertTriangle size={14} className="text-amber-500 shrink-0" />,
  fail:    <XCircle size={14} className="text-red-500 shrink-0" />,
};

const STATUS_BG = {
  pass:    "bg-emerald-50 border-emerald-200",
  warning: "bg-amber-50 border-amber-200",
  fail:    "bg-red-50 border-red-200",
};

const OUTCOME_CONFIG = {
  eligible:               { label: "Eligible",                color: "emerald", bg: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  conditionally_eligible: { label: "Conditionally Eligible",  color: "amber",   bg: "bg-amber-50 border-amber-200 text-amber-700" },
  not_eligible:           { label: "Not Eligible",            color: "red",     bg: "bg-red-50 border-red-200 text-red-700" },
};

export default function FIEligibilityGate() {
  const navigate = useNavigate();
  const [assessmentId, setAssessmentId] = useState("");
  const [result,   setResult]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [searched, setSearched] = useState(false);

  const runAssessment = async () => {
    if (!assessmentId) { toast.error("Enter a Project Assessment ID."); return; }
    setLoading(true);
    try {
      const res = await client.post("/fi/eligibility/assessment", {
        assessment_id: parseInt(assessmentId),
      });
      setResult(res.data);
      setSearched(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assessment failed.");
    }
    setLoading(false);
  };

  const eligibility = result?.eligibility;
  const cfg = eligibility ? OUTCOME_CONFIG[eligibility.outcome] || OUTCOME_CONFIG.not_eligible : null;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="FINANCIAL INSTITUTION"
        title="Climate Finance Eligibility — Gate 0"
        subtitle="Mandatory gateway. No deal proceeds to structuring without passing eligibility."
        backUrl="/fi"
        backLabel="FI Workspace"
      />

      <MetricCard label="Eligibility Checks" value={5} color="text-slate-900" />

      {/* 5 checks overview */}
      <div className="bg-slate-900 rounded-xl p-5">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">5 Eligibility Checks</p>
        <div className="grid grid-cols-1 gap-2">
          {[
            "1. Emissions Baseline & Reduction Potential",
            "2. Climate Impact Classification",
            "3. Additionality Screening",
            "4. Data Credibility (Confidence + Evidence)",
            "5. Regulatory Alignment (GHG Protocol)",
          ].map((check, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-teal-400 shrink-0" />
              <p className="text-xs text-slate-300">{check}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Input */}
      <SectionCard title="Run Assessment">
        <div className="flex gap-3">
          <input
            type="number"
            value={assessmentId}
            onChange={e => setAssessmentId(e.target.value)}
            placeholder="Project Assessment ID (e.g. 42)"
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400"
            data-testid="eligibility-assessment-id"
          />
          <CTAButton variant="action" icon={loading ? RefreshCw : Shield} loading={loading} onClick={runAssessment} data-testid="run-eligibility-btn">
            Run Gate 0
          </CTAButton>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Find the assessment ID in the Project workspace or FI Pipeline → Deal detail.
        </p>
      </SectionCard>

        {/* Result */}
        {eligibility && cfg && (
          <div data-testid="eligibility-result">
            <div className={`rounded-xl border p-5 mb-4 ${cfg.bg}`}>
              <p className="text-xs font-bold uppercase tracking-wider mb-1 opacity-70">Gate 0 Outcome</p>
              <p className="text-xl font-bold">{cfg.label}</p>
              <p className="text-xs mt-1 opacity-70">{eligibility.outcome_description}</p>
              <div className="flex gap-3 mt-2 text-xs font-bold opacity-70">
                <span>Score: {eligibility.eligibility_score}/100</span>
                <span>Passed: {eligibility.checks_passed}</span>
                <span>Warnings: {eligibility.checks_warning}</span>
                <span>Failed: {eligibility.checks_failed}</span>
              </div>
            </div>

            {/* Check details */}
            <div className="space-y-2 mb-4">
              {eligibility.checks?.map((check) => (
                <div key={check.check} className={`rounded-xl border px-4 py-3 ${STATUS_BG[check.status] || "bg-slate-50 border-slate-200"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {STATUS_ICONS[check.status] || STATUS_ICONS.warning}
                    <p className="text-xs font-bold text-slate-700">{check.label}</p>
                    <span className="ml-auto text-xs font-bold opacity-60">{check.score}/100</span>
                  </div>
                  <p className="text-xs text-slate-600">{check.finding}</p>
                  {check.action && <p className="text-xs text-amber-700 mt-1 font-medium">→ {check.action}</p>}
                </div>
              ))}
            </div>

            {eligibility.required_actions?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-xs font-bold text-amber-700 mb-2">Required Actions Before Advancing</p>
                {eligibility.required_actions.map((a, i) => (
                  <p key={i} className="text-xs text-amber-700 mb-1">→ {a}</p>
                ))}
              </div>
            )}

            {eligibility.can_proceed_to_structuring && (
              <button onClick={() => navigate("/fi/structuring")}
                className="w-full py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700">
                Proceed to Deal Structuring →
              </button>
            )}

            <p className="text-xs text-slate-400 italic mt-3 text-center">{eligibility.disclaimer}</p>
          </div>
        )}
    </div>
  );
}
